import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { adminFixtureDeposits, useAdminFixturesFromQuery } from '../fixtures'
import type { AdminDeposit } from '../types'
import { money } from '../../client/prediction-filter'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { formatWhen, safeAdminError } from '../safe-ui'

/** Finance activity derived from existing admin deposits API — not a fake ledger. */
export function AdminTransactionsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<AdminDeposit[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (fixtures) {
          if (!cancelled) setRows(adminFixtureDeposits)
          return
        }
        const data = await adminApi.listDeposits({ page: 1, pageSize: 50 })
        if (!cancelled) setRows(data.deposits || [])
      } catch (err) {
        if (!cancelled) setError(safeAdminError(err, 'Unable to load transactions'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fixtures])

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Transactions" title="Finance activity">
        <p className="cp-muted">Currently deposit-derived records only.</p>
      </PageHeader>

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Preview deposit-derived rows only.
        </StateBlock>
      ) : null}

      <StateBlock tone="info">
        There is no standalone admin transactions ledger. This view lists deposit records from the deposits API.
        Withdrawals are not included.
      </StateBlock>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? <p className="cp-error">{error}</p> : null}

      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rows={rows}
          rowKey={(d) => String(d.id)}
          empty={loading ? 'Loading…' : 'No deposit-derived transactions.'}
            columns={[
              { key: 'ref', header: 'Reference', render: (d) => d.reference || '—' },
              {
                key: 'client',
                header: 'Client',
                render: (d) =>
                  d.client_id ? (
                    <Link to={`/admin/clients/${d.client_id}${q}`}>
                      {d.client_email || d.client_name || `#${d.client_id}`}
                    </Link>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'account',
                header: 'Account',
                render: (d) =>
                  d.trading_account_id ? (
                    <Link to={`/admin/trading-accounts/${d.trading_account_id}${q}`}>
                      #{d.trading_account_id}
                    </Link>
                  ) : (
                    '—'
                  ),
              },
              { key: 'type', header: 'Type', render: () => 'Deposit' },
              {
                key: 'amount',
                header: 'Amount',
                render: (d) => `${money(d.amount)} ${d.currency || 'USD'}`,
              },
              { key: 'provider', header: 'Provider', render: (d) => d.provider || '—' },
              { key: 'status', header: 'Status', render: (d) => d.status || '—' },
              { key: 'created', header: 'Created', render: (d) => formatWhen(d.created_at) },
              {
                key: 'completed',
                header: 'Completed',
                render: (d) => formatWhen(d.completed_at),
              },
            ]}
          />
        <div className="cp-platform-actions" style={{ marginTop: '1rem' }}>
          <Button to={`/admin/deposits${q}`} variant="secondary">
            Open deposit queue
          </Button>
        </div>
      </div>
    </section>
  )
}
